/**
 * Portal Categories Page
 * Category management for shop owners
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  FiShoppingCart, FiShoppingBag, FiTag, FiPackage, FiHome,
  FiBook, FiHeadphones, FiMonitor, FiSmartphone, FiCamera,
  FiMusic, FiFilm, FiTool, FiTruck, FiGift, FiStar,
  FiHeart, FiSun, FiDroplet, FiCoffee, FiSearch,
} from 'react-icons/fi';
import {
  MdRestaurant, MdPets, MdSportsSoccer, MdFitnessCenter,
  MdLocalPharmacy, MdSchool, MdFace, MdChildCare, MdToys,
  MdHotel, MdDiamond, MdWatch, MdChair, MdKitchen,
  MdLocalFlorist, MdSpa, MdBeachAccess, MdDirectionsCar,
  MdCategory, MdStorefront,
} from 'react-icons/md';
import portalService from '../../services/portal.service';
import type { PortalProductCategory } from '../../types/portal.types';

// Icon registry for the picker
const ICON_MAP: Record<string, React.ElementType> = {
  // Shopping
  ShoppingCart: FiShoppingCart, ShoppingBag: FiShoppingBag, Tag: FiTag,
  Package: FiPackage, Storefront: MdStorefront, Category: MdCategory,
  // Books & Education
  Book: FiBook, School: MdSchool,
  // Tech & Electronics
  Headphones: FiHeadphones, Monitor: FiMonitor, Smartphone: FiSmartphone,
  Camera: FiCamera,
  // Entertainment
  Music: FiMusic, Film: FiFilm,
  // Food & Drinks
  Restaurant: MdRestaurant, Coffee: FiCoffee,
  // Fashion & Accessories
  Diamond: MdDiamond, Watch: MdWatch,
  // Health & Wellness
  Pharmacy: MdLocalPharmacy, Fitness: MdFitnessCenter, Spa: MdSpa, Heart: FiHeart,
  // Home & Furniture
  Home: FiHome, Chair: MdChair, Kitchen: MdKitchen,
  // Nature & Travel
  Florist: MdLocalFlorist, Beach: MdBeachAccess, Sun: FiSun, Droplet: FiDroplet,
  // Beauty
  Face: MdFace,
  // Sports & Kids
  Sports: MdSportsSoccer, ChildCare: MdChildCare, Toys: MdToys, Pets: MdPets,
  // Other
  Hotel: MdHotel, Car: MdDirectionsCar, Tool: FiTool, Truck: FiTruck,
  Gift: FiGift, Star: FiStar,
};

const ICON_LABELS: Record<string, string> = {
  ShoppingCart: 'Carrinho', ShoppingBag: 'Saco', Tag: 'Etiqueta',
  Package: 'Embalagem', Storefront: 'Montra', Category: 'Categoria',
  Book: 'Livro', School: 'Escola',
  Headphones: 'Auscultadores', Monitor: 'Monitor', Smartphone: 'Telemóvel', Camera: 'Câmara',
  Music: 'Música', Film: 'Cinema',
  Restaurant: 'Restaurante', Coffee: 'Café',
  Diamond: 'Diamante', Watch: 'Relógio',
  Pharmacy: 'Farmácia', Fitness: 'Ginásio', Spa: 'Spa', Heart: 'Saúde',
  Home: 'Casa', Chair: 'Cadeira', Kitchen: 'Cozinha',
  Florist: 'Flores', Beach: 'Praia', Sun: 'Sol', Droplet: 'Água',
  Face: 'Beleza',
  Sports: 'Desporto', ChildCare: 'Crianças', Toys: 'Brinquedos', Pets: 'Animais',
  Hotel: 'Hotel', Car: 'Carro', Tool: 'Ferramentas', Truck: 'Entrega',
  Gift: 'Presente', Star: 'Destaque',
};

const PortalCategories: React.FC = () => {
  const [categories, setCategories] = useState<PortalProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCat, setEditingCat] = useState<PortalProductCategory | null>(null);
  const [nome, setNome] = useState('');
  const [catImagem, setCatImagem] = useState<string | undefined>();
  const [catMimeType, setCatMimeType] = useState<string | undefined>();
  const [catIcone, setCatIcone] = useState<string>('');
  const [iconSearch, setIconSearch] = useState('');

  const filteredIcons = useMemo(() => {
    const q = iconSearch.toLowerCase();
    return Object.keys(ICON_MAP).filter(name =>
      !q || name.toLowerCase().includes(q) || (ICON_LABELS[name] || '').toLowerCase().includes(q)
    );
  }, [iconSearch]);

  const handleCatImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setCatImagem(base64);
      setCatMimeType(file.type);
    };
    reader.readAsDataURL(file);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await portalService.getProductCategories();
      setCategories(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openModal = (cat?: PortalProductCategory) => {
    if (cat) {
      setEditingCat(cat);
      setNome(cat.NOME);
      setCatIcone(cat.ICONE || '');
    } else {
      setEditingCat(null);
      setNome('');
      setCatIcone('');
    }
    setCatImagem(undefined);
    setCatMimeType(undefined);
    setIconSearch('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCat) {
        await portalService.updateCategory(editingCat.ID, {
          nome,
          imagem: catImagem,
          mimeType: catMimeType,
          icone: catIcone || undefined,
        });
      } else {
        await portalService.createCategory({
          nome,
          imagem: catImagem,
          mimeType: catMimeType,
          icone: catIcone || undefined,
        });
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta categoria?')) return;
    try {
      await portalService.deleteCategory(id);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  const SelectedIcon = catIcone ? ICON_MAP[catIcone] : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias</h1>
          <p className="text-gray-600">Organize os seus produtos por categorias</p>
        </div>
        <button
          onClick={() => openModal()}
          className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Categoria
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            Nenhuma categoria. Crie a primeira para organizar os seus produtos.
          </div>
        ) : (
          categories.map((cat) => {
            const IconComp = cat.ICONE ? ICON_MAP[cat.ICONE] : null;
            return (
              <div key={cat.ID} className="bg-white rounded-lg shadow p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {IconComp ? (
                    <div className="w-10 h-10 rounded-lg bg-pagali-primary/10 flex items-center justify-center text-pagali-primary">
                      <IconComp size={20} />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                      <MdCategory size={20} />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{cat.NOME}</h3>
                    <p className="text-sm text-gray-500">{(cat as any).totalProdutos || 0} produto(s)</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(cat)}
                    className="text-pagali-primary hover:text-pagali-primary/80 text-sm"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(cat.ID)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{editingCat ? 'Editar Categoria' : 'Nova Categoria'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text" required value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  placeholder="Ex: Electrónica, Roupa, Livros..."
                />
              </div>

              {/* Icon picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ícone
                  {SelectedIcon && (
                    <span className="ml-2 inline-flex items-center gap-1 text-pagali-primary font-normal">
                      <SelectedIcon size={14} />
                      <span className="text-xs">{ICON_LABELS[catIcone] || catIcone}</span>
                    </span>
                  )}
                </label>
                {/* Search */}
                <div className="relative mb-2">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    placeholder="Pesquisar ícone..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                {/* Grid */}
                <div className="grid grid-cols-8 gap-1 p-2 border border-gray-200 rounded-lg max-h-44 overflow-y-auto">
                  {/* No icon option */}
                  <button
                    type="button"
                    title="Sem ícone"
                    onClick={() => setCatIcone('')}
                    className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                      catIcone === ''
                        ? 'border-2 border-pagali-primary bg-pagali-primary/10'
                        : 'border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-300 text-xs leading-none">—</span>
                  </button>
                  {filteredIcons.map(name => {
                    const IconComp = ICON_MAP[name];
                    const selected = catIcone === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        title={ICON_LABELS[name] || name}
                        onClick={() => setCatIcone(name)}
                        className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                          selected
                            ? 'border-2 border-pagali-primary bg-pagali-primary/10 text-pagali-primary'
                            : 'border-2 border-transparent hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <IconComp size={18} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Imagem da Categoria</label>
                <div className="flex items-center gap-4">
                  {catImagem && (
                    <img
                      src={`data:${catMimeType || 'image/png'};base64,${catImagem}`}
                      alt="Preview"
                      className="h-14 w-14 rounded-lg object-cover border border-gray-200"
                    />
                  )}
                  <label className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700">
                    {catImagem ? 'Alterar' : 'Carregar Foto'}
                    <input type="file" accept="image/*" onChange={handleCatImageChange} className="hidden" />
                  </label>
                  {catImagem && (
                    <button type="button" onClick={() => { setCatImagem(undefined); setCatMimeType(undefined); }}
                      className="text-sm text-red-500">Remover</button>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80">
                  {editingCat ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalCategories;
